import { useState } from "react";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { useQueryClient } from "react-query";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { PERMISSION_TRAINING_STUDENT_OBSERVATIONS } from "../../../../permissions";
import studentsAPI from "../../../../services/students";

// Staff actions on an observation ("Notifier les parents" / delete), shared by
// the student sheet and the admin tracking page. The caller must render a
// <ConfirmPopup /> and provide a Toast ref.
export const useStudentObservationActions = ({ schoolId, trainingId, accessToken = "", toast }) => {
  const queryClient = useQueryClient();
  const { hasPermission, currentMembership } = useAuthContext();
  const canCreate = hasPermission(PERMISSION_TRAINING_STUDENT_OBSERVATIONS.CREATE);
  const canDeleteAny = hasPermission(PERMISSION_TRAINING_STUDENT_OBSERVATIONS.DELETE);
  const canNotify = hasPermission(PERMISSION_TRAINING_STUDENT_OBSERVATIONS.NOTIFY);
  const [notifyingId, setNotifyingId] = useState(null);

  const canDelete = (observation) =>
    canDeleteAny ||
    (canCreate && !observation?.notified && !!currentMembership?.id && observation?.author_member_id === currentMembership.id);

  const canNotifyObservation = (observation) => canNotify && !!observation?.can_notify;

  const hasActions = (observation) => canNotifyObservation(observation) || canDelete(observation);

  const invalidate = (observation) => {
    queryClient.invalidateQueries(["student-observations", schoolId, trainingId, observation?.student_id]);
    queryClient.invalidateQueries(["training-student-observations", schoolId, trainingId]);
  };

  const handleNotify = (event, observation) => {
    confirmPopup({
      target: event.currentTarget,
      message: observation?.notified
        ? "Renvoyer l'email de notification aux parents ?"
        : "L'observation sera visible dans l'espace famille et un email sera envoyé immédiatement aux parents. Continuer ?",
      icon: "pi pi-send",
      acceptLabel: "Notifier",
      rejectLabel: "Annuler",
      accept: () => {
        setNotifyingId(observation.id);
        studentsAPI
          .NotifyObservation(schoolId, trainingId, accessToken, observation.student_id, observation.id)
          .then((result) => {
            const status = result?.data?.notification_status;
            if (status === "SENT") {
              toast?.current?.show({ severity: "success", summary: "Parents notifiés", detail: "L'observation est visible dans l'espace famille." });
            } else {
              toast?.current?.show({
                severity: "warn",
                summary: "Observation partagée",
                detail:
                  status === "FAILED"
                    ? "L'observation est visible dans l'espace famille, mais l'envoi de l'email a échoué."
                    : "L'observation est visible dans l'espace famille, mais aucun email n'a été envoyé.",
              });
            }
          })
          .catch((err) => toast?.current?.show({ severity: "error", summary: "Erreur", detail: err.message }))
          .finally(() => {
            setNotifyingId(null);
            invalidate(observation);
          });
      },
    });
  };

  const handleDelete = (event, observation) => {
    confirmPopup({
      target: event.currentTarget,
      message: observation?.notified
        ? "Cette observation est visible par la famille. La supprimer quand même ?"
        : "Supprimer cette observation interne ?",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Supprimer",
      rejectLabel: "Annuler",
      acceptClassName: "p-button-danger",
      accept: () => {
        studentsAPI
          .DeleteObservation(schoolId, trainingId, accessToken, observation.student_id, observation.id)
          .then(() => {
            invalidate(observation);
            toast?.current?.show({ severity: "success", summary: "Observation supprimée" });
          })
          .catch((err) => toast?.current?.show({ severity: "error", summary: "Erreur", detail: err.message }));
      },
    });
  };

  const renderActions = (observation) => (
    <div className="flex align-items-center gap-1">
      {canNotifyObservation(observation) && (
        <Button
          icon="pi pi-send"
          label={observation?.notified ? "Renvoyer" : "Notifier les parents"}
          size="small"
          outlined
          loading={notifyingId === observation.id}
          disabled={!!notifyingId}
          onClick={(event) => handleNotify(event, observation)}
        />
      )}
      {canDelete(observation) && (
        <Button
          icon="pi pi-trash"
          rounded
          text
          severity="danger"
          aria-label="Supprimer l'observation"
          onClick={(event) => handleDelete(event, observation)}
        />
      )}
    </div>
  );

  return { canCreate, canDelete, hasActions, renderActions };
};
